import { Check, ChevronDown, MapPin, Search } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranch } from '@/features/branch/branch-context';
import styles from './BranchSwitcher.module.css';

/**
 * Compact, searchable branch selector for the topbar (RL-1303): a button with
 * the current branch opens a small panel with a filter box and the branches
 * as a listbox. Behaviour is unchanged: the branches come from the role
 * assignments, "All branches" only for group roles, and the selection drives
 * X-Branch-Id (BranchProvider). Keyboard: Enter/Space/↓ opens, typing
 * filters, ↑/↓ move, Enter chooses, Esc closes back to the button.
 */
export function BranchSwitcher() {
  const { t } = useTranslation();
  const { branches, selected, canSelectAll, select } = useBranch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const outside = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  if (branches.length === 0) return null;

  const options = [
    ...branches.map((b) => ({ id: b.id, label: b.name })),
    ...(canSelectAll
      ? [{ id: 'all', label: t('shell.allBranchesOption') }]
      : []),
  ];
  const currentId = selected?.id ?? 'all';
  const current = options.find((o) => o.id === currentId) ?? options[0];
  const term = query.trim().toLowerCase();
  const shown = term
    ? options.filter((o) => o.label.toLowerCase().includes(term))
    : options;
  const optionId = (id: string) => `${listId}-${id}`;

  const openPanel = () => {
    setQuery('');
    setActive(Math.max(0, options.indexOf(current)));
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };
  const choose = (id: string) => {
    if (id !== currentId) select(id);
    close();
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openPanel();
    }
  };
  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!shown.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = shown[Math.min(active, shown.length - 1)];
      if (o) choose(o.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      close(false);
    }
  };
  const activeOption = shown[Math.min(active, shown.length - 1)];

  return (
    <div ref={wrap} className={styles.wrap}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('shell.branchCurrent', { name: current.label })}
        title={current.label}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onTriggerKey}
      >
        <MapPin size={15} aria-hidden="true" className={styles.pin} />
        <span className={styles.current}>{current.label}</span>
        <ChevronDown size={15} aria-hidden="true" className={styles.chev} />
      </button>
      {open && (
        <div className={styles.panel}>
          <label className={styles.filter}>
            <Search size={15} aria-hidden="true" />
            <input
              ref={input}
              className={styles.filterInput}
              type="text"
              role="combobox"
              aria-label={t('shell.findBranch')}
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                activeOption ? optionId(activeOption.id) : undefined
              }
              placeholder={t('shell.findBranch')}
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onInputKey}
            />
          </label>
          <ul
            id={listId}
            role="listbox"
            aria-label={t('shell.branch')}
            className={styles.list}
          >
            {shown.map((o) => (
              <li
                key={o.id}
                id={optionId(o.id)}
                role="option"
                aria-selected={o.id === currentId}
                data-active={o === activeOption || undefined}
                className={styles.option}
                onPointerDown={(e) => e.preventDefault()}
                onPointerEnter={() => setActive(shown.indexOf(o))}
                onClick={() => choose(o.id)}
              >
                <span>{o.label}</span>
                {o.id === currentId && (
                  <Check size={15} aria-hidden="true" className={styles.tick} />
                )}
              </li>
            ))}
          </ul>
          {shown.length === 0 && (
            <p className={styles.empty}>{t('shell.noBranchMatch')}</p>
          )}
        </div>
      )}
    </div>
  );
}
